import { useState, useEffect, useRef } from 'react';
import { Building2, Upload, Save, FileText } from 'lucide-react';
import { getMyCompany, updateMyCompany, uploadCompanyLogo } from '../api/companies';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export function CompanySettingsPage() {
  const [company, setCompany] = useState(null);
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [billing, setBilling] = useState({
    billing_name: '', billing_tax_number: '', billing_tax_office: '',
    billing_address: '', billing_city: '', billing_district: '', billing_zip: '',
    billing_email: '', billing_phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    getMyCompany().then(d => {
      const c = d.company;
      setCompany(c);
      setForm({ name: c.name || '', email: c.email || '', phone: c.phone || '' });
      setBilling({
        billing_name: c.billing_name || '', billing_tax_number: c.billing_tax_number || '',
        billing_tax_office: c.billing_tax_office || '', billing_address: c.billing_address || '',
        billing_city: c.billing_city || '', billing_district: c.billing_district || '',
        billing_zip: c.billing_zip || '', billing_email: c.billing_email || '',
        billing_phone: c.billing_phone || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = tab === 'billing' ? billing : form;
      const d = await updateMyCompany(payload);
      setCompany(d.company);
      setSuccess('Bilgiler güncellendi');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Güncelleme başarısız');
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const d = await uploadCompanyLogo(formData);
      setCompany(c => ({ ...c, logo_url: d.logo_url }));
    } catch {
      setError('Logo yüklenemedi');
    }
    setUploading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  const set = (obj, setObj) => (key) => (e) => setObj(f => ({ ...f, [key]: e.target.value }));

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-1 flex items-center gap-2">
        <Building2 size={22} /> Firma Ayarları
      </h1>
      <p className="text-sm text-slate-500 mb-6">Firma bilgilerinizi düzenleyin</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl border border-slate-200 p-1 w-fit">
        <button onClick={() => setTab('general')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition ${tab === 'general' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
          <Building2 size={14} className="inline mr-1.5 -mt-0.5" /> Genel
        </button>
        <button onClick={() => setTab('billing')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition ${tab === 'billing' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
          <FileText size={14} className="inline mr-1.5 -mt-0.5" /> Fatura Bilgileri
        </button>
      </div>

      {tab === 'general' && (
        <>
          {/* Logo */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h3 className="font-semibold text-slate-700 mb-4">Firma Logosu</h3>
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
                {company?.logo_url ? (
                  <img src={company.logo_url} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <Building2 size={32} className="text-slate-300" />
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleLogoUpload} />
                <Button variant="secondary" size="sm" loading={uploading} onClick={() => fileRef.current?.click()}>
                  <Upload size={14} /> Logo Yükle
                </Button>
                <p className="text-xs text-slate-400 mt-2">PNG, JPEG, SVG veya WebP. Maks 2MB.</p>
              </div>
            </div>
          </div>

          {/* Genel Bilgiler */}
          <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-semibold text-slate-700 mb-2">Firma Bilgileri</h3>
            <Input label="Firma Adı" value={form.name} onChange={set(form, setForm)('name')} />
            <Input label="E-posta" type="email" value={form.email} onChange={set(form, setForm)('email')} />
            <Input label="Telefon" value={form.phone} onChange={set(form, setForm)('phone')} placeholder="5xx xxx xx xx" />
            {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg">{error}</div>}
            {success && <div className="bg-green-50 text-green-600 text-sm px-3 py-2 rounded-lg">{success}</div>}
            <div className="flex justify-end pt-2">
              <Button type="submit" loading={saving}><Save size={14} /> Kaydet</Button>
            </div>
          </form>
        </>
      )}

      {tab === 'billing' && (
        <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-semibold text-slate-700 mb-2">Fatura Bilgileri</h3>
          <Input label="Firma Ünvanı" value={billing.billing_name} onChange={set(billing, setBilling)('billing_name')} placeholder="ABC Saha Hizmetleri Ltd. Şti." />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Vergi Numarası" value={billing.billing_tax_number} onChange={set(billing, setBilling)('billing_tax_number')} placeholder="1234567890" />
            <Input label="Vergi Dairesi" value={billing.billing_tax_office} onChange={set(billing, setBilling)('billing_tax_office')} placeholder="Kadıköy" />
          </div>
          <Input label="Fatura Adresi" value={billing.billing_address} onChange={set(billing, setBilling)('billing_address')} placeholder="Cadde, Sokak, No, Kat" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="İl" value={billing.billing_city} onChange={set(billing, setBilling)('billing_city')} placeholder="İstanbul" />
            <Input label="İlçe" value={billing.billing_district} onChange={set(billing, setBilling)('billing_district')} placeholder="Kadıköy" />
            <Input label="Posta Kodu" value={billing.billing_zip} onChange={set(billing, setBilling)('billing_zip')} placeholder="34700" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Fatura E-postası" type="email" value={billing.billing_email} onChange={set(billing, setBilling)('billing_email')} placeholder="muhasebe@firma.com" />
            <Input label="Fatura Telefonu" value={billing.billing_phone} onChange={set(billing, setBilling)('billing_phone')} placeholder="5xx xxx xx xx" />
          </div>
          {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg">{error}</div>}
          {success && <div className="bg-green-50 text-green-600 text-sm px-3 py-2 rounded-lg">{success}</div>}
          <div className="flex justify-end pt-2">
            <Button type="submit" loading={saving}><Save size={14} /> Kaydet</Button>
          </div>
        </form>
      )}
    </div>
  );
}
